import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LoginPage from "../pages/auth/LoginPage";      
import RegisterPage from "../pages/auth/RegisterPage";
import VerifyPage from "../pages/auth/VerifyPage";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";
import EmployeeRegister from "../pages/EmployeeRegister";
import DashboardHome from "../pages/dashboard/DashboardHome";
import Layout from "../components/layout/Layout";
import Leads from "../pages/dashboard/Leads";
import People from "../pages/dashboard/People";
import Projects from "../pages/dashboard/Projects";
import Schedule from "../pages/dashboard/Schedule";
import Timesheets from "../pages/dashboard/Timesheets";
import LeaveManager from "../pages/dashboard/LeaveManager";
import Settings from "../pages/dashboard/Settings";
import Directory from "../pages/dashboard/Directory";
import Notifications from "../pages/dashboard/Notifications";
import ProtectedRoute from "../components/protected/ProtectedRoute";
import AdminLoginPage from "../pages/admin/AdminLoginPage";
import AdminDashboardPage from "../pages/admin/AdminDashboardPage";

export default function AppRoutes() {
  const { isValid, loading } = useAuth();

  // Show nothing while loading
  if (loading) {
    return null;
  }

  return (
    <Routes>
      {/* Root - redirect based on auth status */}
      <Route
        path="/"
        element={
          isValid ? (
            (() => {
              return <Navigate to="/dashboard" replace />;
            })()
          ) : (
            (() => {
              return <Navigate to="/login" replace />;
            })()
          )
        }
      />

      {/* Login - redirect to dashboard if already authenticated */}
      <Route
        path="/login"
        element={
          isValid ? (
            (() => {
              return <Navigate to="/dashboard" replace />;
            })()
          ) : (
            <LoginPage />
          )
        }
      />

      {/* Register - redirect to dashboard if already authenticated */}
      <Route
        path="/register"
        element={
          isValid ? (
            (() => {
              return <Navigate to="/dashboard" replace />;
            })()
          ) : (
            <RegisterPage />
          )
        }
      />

      {/* Public routes */}
      <Route path="/verify" element={<VerifyPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/employee-register" element={<EmployeeRegister />} />

      {/* Main Dashboard - protected with nested routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard Home - any authenticated user */}
        <Route index element={<DashboardHome />} />
        
        {/* Settings - any authenticated user can access */}
        <Route 
          path="settings" 
          element={<Settings />} 
        />
        
        {/* Leads - requires view_leads permission */}
        <Route 
          path="leads" 
          element={
            <ProtectedRoute requiredPermission="view_leads">
              <Leads />
            </ProtectedRoute>
          } 
        />
        
        {/* Projects - requires view_projects permission */}
        <Route 
          path="projects" 
          element={
            <ProtectedRoute requiredPermission={["view_projects", "view_assigned_projects"]}>
              <Projects />
            </ProtectedRoute>
          } 
        />
        
        {/* People - requires view_people permission */}
        <Route 
          path="people" 
          element={
            <ProtectedRoute requiredPermission="view_people">
              <People />
            </ProtectedRoute>
          } 
        />
        
        {/* Directory - requires view_internal_directory OR view_external_directory permission */}
        <Route 
          path="directory" 
          element={
            <ProtectedRoute requiredPermission={["view_internal_directory", "view_external_directory"]}>
              <Directory />
            </ProtectedRoute>
          } 
        />
        
        {/* Schedule - requires view_schedule OR view_own_schedule */}
        <Route 
          path="schedule" 
          element={
            <ProtectedRoute requiredPermission={['view_schedule', 'view_own_schedule']}>
              <Schedule />
            </ProtectedRoute>
          } 
        />
        
        {/* Timesheets - requires view_timesheets OR view_own_timesheets */}
        <Route 
          path="timesheets" 
          element={
            <ProtectedRoute requiredPermission={['view_timesheets', 'view_own_timesheets']}>
              <Timesheets />
            </ProtectedRoute>
          } 
        />

        {/* Notifications - any authenticated user */}
        <Route
          path="notifications"
          element={<Notifications />}
        />
        
        {/* Leave Manager - requires time off permissions */}
        <Route 
          path="leave-manager" 
          element={
            <ProtectedRoute requiredPermission={['timeoff:view:self', 'timeoff:view:all', 'timeoff:approve']}>
              <LeaveManager />
            </ProtectedRoute>
          } 
        />
      </Route>

      {/* Admin (separate auth, own token) */}
      <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboardPage />} />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}